import Swal from "sweetalert2";
import ApiService, { HttpMethod } from "../../../commons/api/api_service";
import BaseResponse from "../../../commons/api/base_response";
import { Endpoints } from "../../../commons/api/endpoints";
import { ProviderVM } from "../../../commons/base/providers/view_model";
import { Pageable, Paging } from "../../../commons/models/Page";
import {
    ShowErrorViewModel,
    ShowSnackbarModel,
} from "../../../commons/scaffold/scaffold";
import { MaterialType } from "../../commons/enums";
import {
    FormBomModel,
    SearchResultItemRequest,
    SubmitBomModel,
    SubmitFormModel,
} from "../models/bom_request";
import {
    BomResponse,
    MasterItemModel,
    MappedItemOptionModel,
    ResultItemData,
} from "../models/bom_response";

export default class BomDashboardViewModel extends ProviderVM {
    isLoading: boolean = false;
    snackbarModel?: ShowSnackbarModel;
    errorModel?: ShowErrorViewModel;
    isShowModal: boolean = false;
    materialTypes: MaterialType[] = (
        Object.values(MaterialType) as MaterialType[]
    ).filter(
        (type) =>
            type !== MaterialType.RAW_MATERIAL &&
            type !== MaterialType.PACKAGING_RAW
    );
    paging: Paging = {
        currentPage: 1,
        size: 20,
        totalPage: 1,
    };
    filterKeyword?: string;
    filterMaterialType?: MaterialType;
    resultItemList: ResultItemData[] = []; // paged result list
    existingResultItemIds: number[] = [];
    formAction: string = "CREATE";
    formIsEditing: boolean = false;
    formResultItemId?: number;
    formResultMaterialType?: MaterialType;
    formResultYield: number = 0;
    formBomMap: Map<string, FormBomModel> = new Map<string, FormBomModel>();
    formAddedItemIds: number[] = [];
    masterItemMap: Map<number, MasterItemModel> = new Map<
        number,
        MasterItemModel
    >();
    resultItemOptions: MappedItemOptionModel[] = [];
    materialItemOptions: MappedItemOptionModel[] = [];
    formSelectedAddItemId?: number;

    constructor() {
        super();
        this.setup();
    }

    setup() {
        this.fetchResultList();
        this.fetchItemOptions();
    }

    // Fetch Data
    fetchResultList() {
        this.setIsLoading(true);

        const queryParameters: SearchResultItemRequest = {
            page: this.paging.currentPage,
            size: this.paging.size,
            keyword: this.filterKeyword,
            materialType: this.filterMaterialType,
        };

        ApiService.fetchWithQueryParams(Endpoints.bomDashboardHomepage, {
            httpMethod: HttpMethod.get,
            queryParameters: Object.fromEntries(
                Object.entries(queryParameters).filter(
                    ([_, value]) => value !== undefined
                )
            ),
            onSuccess: (response: BaseResponse<Pageable<ResultItemData>>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    const data = response.data?.data as ResultItemData[];
                    this.setResultItemList(data);
                    this.setPaging(response.data?.paging ?? this.paging);
                } else {
                    this.setErrorModel({
                        action: () => {
                            this.reset();
                            this.setErrorModel(undefined);
                            this.setup();
                        },
                        message: "Gagal mendapatkan data halaman awal BOM",
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorModel({
                    action: () => {
                        this.setErrorModel(undefined);
                        this.reset();
                        this.setup();
                    },
                    message: "Gagal mendapatkan data halaman awal BOM",
                });
            },
        });
    }

    fetchBOM(masterItemId: number) {
        this.setIsLoading(true);
        ApiService.fetch(
            `${Endpoints.getBomDetail}/${masterItemId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (response: BaseResponse<BomResponse[]>) => {
                    this.setIsLoading(false);
                    if (response.code === 200) {
                        const bomMap = new Map<string, FormBomModel>();
                        const addedItemIds: number[] = [];
                        response.data?.forEach((bom) => {
                            const uniqueId = `${bom.resultItemId}-${bom.componentItemId}`;

                            bomMap.set(uniqueId, {
                                resultItemId: bom.resultItemId,
                                componentItemId: bom.componentItemId,
                                uniqueId: uniqueId,
                                materialCode: bom.materialCode,
                                masterItemName: bom.masterItemName,
                                bom: bom.bom,
                                smallUnit: bom.smallUnit,
                                isActive: bom.isActive,
                                isNew: false,
                            });
                            addedItemIds.push(bom.componentItemId);
                        });
                        this.setFormBomMap(bomMap);
                        this.setFormAddedItemIds(addedItemIds);
                        this.filterUnaddedMaterialItemOptions();
                    } else {
                        this.setErrorModel({
                            action: () => {
                                this.reset();
                                this.setErrorModel(undefined);
                                this.setup();
                            },
                            message: "Gagal mendapatkan data halaman awal BOM",
                        });
                    }
                },
                onFailure: (error) => {
                    console.log(error);
                    this.setIsLoading(false);
                    this.setErrorModel({
                        action: () => {
                            this.reset();
                            this.setErrorModel(undefined);
                            this.reset();
                            this.setup();
                        },
                        message: "Gagal mendapatkan data halaman awal BOM",
                    });
                },
            }
        );
    }

    fetchItemOptions() {
        this.setIsLoading(true);
        ApiService.fetch(Endpoints.getBomItemsAndUnits, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<MasterItemModel[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    const masterItemList = response.data as MasterItemModel[];
                    const masterItemMap = new Map<number, MasterItemModel>();
                    masterItemList.forEach((item) => {
                        masterItemMap.set(item.id, item);
                    });
                    this.setMasterItemMap(masterItemMap);
                } else {
                    this.setErrorModel({
                        action: () => {
                            this.reset();
                            this.setErrorModel(undefined);
                            this.setup();
                        },
                        message: "Gagal mendapatkan data pilihan item",
                    });
                }
            },
            onFailure: (error) => {
                this.setIsLoading(false);
                console.log(error);
                this.setIsLoading(false);
                this.setErrorModel({
                    action: () => {
                        this.reset();
                        this.setErrorModel(undefined);
                        this.reset();
                        this.setup();
                    },
                    message: "Gagal mendapatkan data pilihan item",
                });
            },
        });
    }

    fetchExistingResultItemIds() {
        this.setIsLoading(true);
        ApiService.fetch(Endpoints.getExistingBomResultItemIds, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<number[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    this.setExistingResultItemIds(response.data as number[]);
                } else {
                    this.setErrorModel({
                        action: () => {
                            this.reset();
                            this.setErrorModel(undefined);
                            this.setup();
                        },
                        message:
                            "Gagal mendapatkan daftar id Master Item yang sudah memiliki BOM",
                    });
                }
            },
            onFailure: (error) => {
                this.setIsLoading(false);
                console.log(error);
                this.setIsLoading(false);
                this.setErrorModel({
                    action: () => {
                        this.reset();
                        this.setErrorModel(undefined);
                        this.reset();
                        this.setup();
                    },
                    message:
                        "Gagal mendapatkan daftar id Master Item yang sudah memiliki BOM",
                });
            },
        });
    }

    // Base State Setters
    setIsLoading(value: boolean) {
        this.emit(() => {
            this.isLoading = value;
        });
    }

    setSnackbarModel(value?: ShowSnackbarModel) {
        this.emit(() => {
            this.snackbarModel = value;
        });
    }

    setErrorModel(value?: ShowErrorViewModel) {
        this.emit(() => {
            this.errorModel = value;
        });
    }

    setExistingResultItemIds(value: number[]) {
        this.emit(() => {
            this.existingResultItemIds = value;
        });
    }

    setFormAction(value: string) {
        this.emit(() => {
            this.formAction = value;
        });
    }

    setFormIsEditing(value: boolean) {
        this.emit(() => {
            this.formIsEditing = value;
        });
    }

    setIsShowModal(value: boolean) {
        this.emit(() => {
            this.isShowModal = value;
        });
    }

    setFilterMaterialType(value?: MaterialType) {
        this.emit(() => {
            this.filterMaterialType = value;
        });
    }

    setFilterKeyword(value?: string) {
        this.emit(() => {
            this.filterKeyword = value;
        });
    }

    setResultItemList(value: ResultItemData[]) {
        this.emit(() => {
            this.resultItemList = value;
        });
    }

    setFormResultItemId(value?: number) {
        this.emit(() => {
            this.formResultItemId = value;
        });
    }

    setFormResultMaterialType(value?: MaterialType) {
        this.emit(() => {
            this.formResultMaterialType = value;
        });
    }

    setFormResultYield(value: number) {
        this.emit(() => {
            this.formResultYield = value;
        });
    }

    setFormBomMap(value: Map<string, FormBomModel>) {
        this.emit(() => {
            this.formBomMap = value;
        });
    }

    setFormAddedItemIds(value: number[]) {
        this.emit(() => {
            this.formAddedItemIds = value;
        });
    }

    setMasterItemMap(value: Map<number, MasterItemModel>) {
        this.emit(() => {
            this.masterItemMap = value;
        });
    }

    setMaterialItemOptions(value: MappedItemOptionModel[]) {
        this.emit(() => {
            this.materialItemOptions = value;
        });
    }

    setFormSelecteAddItemId(value?: number) {
        this.emit(() => {
            this.formSelectedAddItemId = value;
        });
    }

    // UI Logic
    updateTableMaterialType(materialType?: MaterialType) {
        this.setFilterMaterialType(materialType);
        this.fetchResultList();
    }

    onOpenModalForCreate() {
        this.setFormAction("CREATE");
        this.setFormIsEditing(true);
        this.fetchExistingResultItemIds();
        this.setIsShowModal(true);
    }

    updateModalOptionsByMaterialType(materialType: MaterialType) {
        this.setFormResultMaterialType(materialType);
        if (this.formResultMaterialType) {
            this.updateResultItemOptions(this.formResultMaterialType);
            this.filterMaterialItemOptionsByResultMaterialType(
                this.formResultMaterialType
            );
        }
    }

    onSelectResultItem(masterItemId: number) {
        const item = this.masterItemMap.get(masterItemId);
        this.setFormResultItemId(masterItemId);
        this.setFormResultYield(item?.yield ?? 0);
    }

    onOpenModalForUpdate(masterItemId: number, masterItemYield: number) {
        const item = this.masterItemMap.get(masterItemId);
        this.fetchBOM(masterItemId);
        this.setFormResultItemId(masterItemId);
        this.setFormResultYield(masterItemYield);
        this.filterMaterialItemOptionsByResultMaterialType(
            item?.materialType as MaterialType
        ); // show only levels below
        this.setFormAction("UPDATE");
        this.setIsShowModal(true);
    }

    closeModal() {
        this.resetFormData();
        this.setFormIsEditing(false);
        this.setIsShowModal(false);
    }

    updateResultItemOptions(materialType: MaterialType) {
        const options: MappedItemOptionModel[] = [
            ...this.masterItemMap.values(),
        ]
            .filter((item) => item.materialType === materialType)
            .filter((item) => !this.existingResultItemIds.includes(item.id))
            .map((item) => ({
                label: `${item.item} - ${item.materialCode}`,
                value: item.id,
            }));
        this.emit(() => {
            this.resultItemOptions = options;
        });
    }

    /**
     * Only show material items when the level is below resultMaterialType
     */
    filterMaterialItemOptionsByResultMaterialType(
        resultMaterialType: MaterialType
    ) {
        const menuLevels = [
            MaterialType.MENU,
            MaterialType.MENU,
            MaterialType.PACKAGING_MENU,
            MaterialType.MENU_PREPARE,
            MaterialType.ASSEMBLY,
            MaterialType.SUB_ASSEMBLY,
            MaterialType.BUMBU,
            MaterialType.RAW_MATERIAL,
            MaterialType.PACKAGING_RAW,
        ];

        const allowedSubLevels: MaterialType[] = [];

        if (resultMaterialType === MaterialType.PACKAGING_MENU) {
            allowedSubLevels.push(MaterialType.PACKAGING_RAW);
        } else if (resultMaterialType === MaterialType.SUB_ASSEMBLY) {
            allowedSubLevels.push(
                MaterialType.SUB_ASSEMBLY,
                MaterialType.BUMBU,
                MaterialType.RAW_MATERIAL
            );
        } else {
            const resultTypeIndex = menuLevels.findIndex(
                (level) => level === resultMaterialType.toString()
            );
            allowedSubLevels.push(...menuLevels.slice(resultTypeIndex + 1));
        }

        const options: MappedItemOptionModel[] = [
            ...this.masterItemMap.values(),
        ]
            .filter((item) =>
                allowedSubLevels.includes(item.materialType as MaterialType)
            )
            .map((option) => ({
                label: `${option.item} - ${option.materialCode}`,
                value: option.id,
            }));

        this.setMaterialItemOptions(options);
    }

    /**
     * Only show material item options which are not added to bom list yet
     */
    filterUnaddedMaterialItemOptions() {
        const options = this.materialItemOptions;
        const existingBomItemIds = [...this.formBomMap.values()].map(
            (item) => item.componentItemId
        );

        const filteredOptions = options.filter(
            (option) => !existingBomItemIds.includes(option.value)
        );

        this.setMaterialItemOptions(filteredOptions);
    }

    handleChangeFormBom(
        uniqueId: string,
        field: keyof FormBomModel,
        value: number | boolean
    ) {
        const updatedMap = new Map(this.formBomMap);
        const bom = updatedMap.get(uniqueId);
        if (bom) {
            updatedMap.set(uniqueId, {
                ...bom,
                isUpdated: !bom.isNew, // if bom is not new and it is changed, then it is an update action
                [field]: value,
            });
        }
        this.setFormBomMap(updatedMap);
    }

    handleAddFormBom(masterItemId: number) {
        if (this.formAddedItemIds.includes(masterItemId)) {
            return;
        }

        const updatedMap = new Map(this.formBomMap);
        const uniqueId = `${Math.random()}-${masterItemId}`;
        const item = [...this.masterItemMap.values()].find(
            (item) => item.id === masterItemId
        );
        updatedMap.set(uniqueId, {
            resultItemId: this.formResultItemId ?? -1,
            componentItemId: item?.id ?? -1,
            uniqueId: uniqueId,
            materialCode: item?.materialCode ?? "",
            masterItemName: item?.item ?? "",
            bom: 0,
            isNew: true,
            isActive: true,
            smallUnit: item?.smallUnit,
        });
        this.setFormBomMap(updatedMap);
        this.setFormAddedItemIds([...this.formAddedItemIds, masterItemId]);

        const materialItemOptions = this.materialItemOptions.filter(
            (option) => !this.formAddedItemIds.includes(option.value)
        );
        this.setMaterialItemOptions(materialItemOptions);
    }

    handleSubmit() {
        const items: SubmitBomModel[] = [...this.formBomMap.values()].map(
            (bom) => ({
                resultItemId: bom.resultItemId,
                componentItemId: bom.componentItemId,
                bom: bom.bom,
                isActive: bom.isActive,
                isNew: bom.isNew,
                isUpdated: bom.isUpdated,
                yield: this.formResultYield,
            })
        );

        if (
            !this.formResultItemId ||
            items.length < 1 ||
            this.formResultYield < 1
        ) {
            this.setErrorModel({
                action: () => {
                    this.resetFormData();
                    this.setIsShowModal(false);
                    this.setErrorModel(undefined);
                },
                message: "Data tidak valid!",
            });
            return;
        }

        const data: SubmitFormModel = {
            resultItemId: this.formResultItemId,
            items: items,
            yield: this.formResultYield,
        };

        Swal.fire({
            title: "Konfirmasi",
            text: "Simpan Data?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Gass",
        }).then((result) => {
            if (!result.isConfirmed) return;

            this.setIsLoading(true);
            ApiService.fetch(Endpoints.saveBom, {
                httpMethod: HttpMethod.post,
                parameters: data,
                onSuccess: (response: BaseResponse<SubmitFormModel>) => {
                    this.setIsLoading(false);
                    if (response.code === 200) {
                        this.setSnackbarModel({
                            message: "Berhasil menyimpan data BOM!",
                            type: "normal",
                            key: new Date().toLocaleString(),
                        });
                        console.log(response.data);
                        this.reset();
                        this.resetFormData();
                        this.setFormIsEditing(false);
                        this.setup();
                    } else {
                        this.setErrorModel({
                            action: () => {
                                this.reset();
                                this.setErrorModel(undefined);
                                this.setup();
                            },
                            message: response.message,
                        });
                    }
                },
                onFailure: (error) => {
                    console.log(error);
                    this.setIsLoading(false);
                    this.setErrorModel({
                        action: () => {
                            this.reset();
                            this.resetFormData();
                            this.setup();
                        },
                        message: error,
                    });
                },
            });
        });
    }

    // Paging
    setPaging(paging: Paging) {
        this.emit(() => {
            this.paging = paging;
        });
    }

    handleChangePage(page: number) {
        this.setPaging({
            currentPage: page,
            size: this.paging?.size || 10,
            totalPage: this.paging?.totalPage ?? 1,
        });
        this.fetchResultList();
    }

    // Reset
    resetFormData() {
        this.emit(() => {
            this.formResultItemId = undefined;
            this.formAddedItemIds = [];
            this.formBomMap = new Map<string, FormBomModel>();
            this.formSelectedAddItemId = undefined;
            this.formResultItemId = undefined;
            this.formResultMaterialType = undefined;
            this.formResultYield = 0;
            this.resultItemOptions = [];
            this.materialItemOptions = [];
        });
    }

    reset() {
        this.emit(() => {
            this.isLoading = false;
            this.errorModel = undefined;
            this.isShowModal = false;
            this.paging = {
                currentPage: 1,
                size: 20,
                totalPage: 1,
            };
            this.filterKeyword = undefined;
            this.filterMaterialType = undefined;
            this.formResultItemId = undefined;
            this.formBomMap = new Map<string, FormBomModel>();
            this.formAddedItemIds = [];
        });
    }
}
